import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import authService from '../services/auth';

export default function VerifyEmailPending() {
  const RESEND_COOLDOWN = 60; // seconds
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  // Start on cooldown: a verification email was just sent at registration.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const navigate = useNavigate();

  useEffect(() => {
    // Get pending email from localStorage
    const pendingEmail = localStorage.getItem('pendingVerificationEmail');
    if (!pendingEmail) {
      // No pending email, redirect to register
      navigate('/register');
      return;
    }
    setEmail(pendingEmail);
  }, [navigate]);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleResendEmail = async () => {
    if (isResending || cooldown > 0) return;
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      await authService.sendVerificationEmail(email);
      setResendSuccess(true);
      setCooldown(RESEND_COOLDOWN); // block re-sends for 60s
    } catch (error: any) {
      setResendError(
        error.response?.data?.message || 'Failed to resend verification email'
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-2">
            <div className="text-6xl">📧</div>
          </div>
          <CardTitle className="text-2xl text-center">
            Verify your email
          </CardTitle>
          <CardDescription className="text-center">
            We've sent a verification link to your email address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {email && (
            <div className="bg-blue-50 border border-blue-200 text-blue-900 text-sm p-4 rounded-md">
              <p className="font-medium mb-1">Email sent to:</p>
              <p className="font-mono">{email}</p>
            </div>
          )}

          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Please check your inbox and click the verification link to activate
              your account.
            </p>
            <p>
              Note: The verification link will expire in 24 hours.
            </p>
          </div>

          {resendSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-900 text-sm p-3 rounded-md">
              Verification email sent successfully! Please check your inbox.
            </div>
          )}

          {resendError && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
              {resendError}
            </div>
          )}

          <div className="pt-2">
            <p className="text-sm text-muted-foreground mb-3">
              Didn't receive the email?
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResendEmail}
              disabled={isResending || cooldown > 0}
            >
              {isResending
                ? 'Sending...'
                : cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : 'Resend Verification Email'}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-sm text-center text-muted-foreground">
            <Link to="/login" className="text-primary hover:underline font-medium">
              Back to Login
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
